import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Activity, AlertTriangle, RotateCcw, Target, TrendingDown, TrendingUp } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeBMI,
  gaugeSegments,
  inToCm,
  cmToIn,
  inchesToFtIn,
  lbToKg,
  kgToLb,
  validateBMI,
  type BMIStandard,
  type CategoryKey,
  type Gender,
} from "@/lib/calculators/bmi";

type UnitSystem = "metric" | "mixed" | "imperial";

/* ------------ Strings ------------ */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "BMI Calculator", bn: "বিএমআই ক্যালকুলেটর" },
  desc: {
    en: "Calculate your Body Mass Index using South Asian or WHO standards. Find your ideal weight range — built for Bangladesh.",
    bn: "দক্ষিণ এশীয় বা WHO মান অনুযায়ী আপনার বিএমআই হিসাব করুন। আদর্শ ওজনের পরিসীমা জানুন — বাংলাদেশের জন্য তৈরি।",
  },

  unitSystem: { en: "Units", bn: "একক" },
  metric: { en: "Metric (kg, cm)", bn: "মেট্রিক (কেজি, সেমি)" },
  mixed: { en: "kg + ft/in", bn: "কেজি + ফিট/ইঞ্চি" },
  imperial: { en: "Imperial (lb, ft)", bn: "ইম্পেরিয়াল (পাউন্ড, ফিট)" },

  standard: { en: "Standard", bn: "মান" },
  asian: { en: "South Asian", bn: "দক্ষিণ এশীয়" },
  who: { en: "WHO Global", bn: "WHO" },

  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },

  age: { en: "Age", bn: "বয়স" },
  ageUnit: { en: "years", bn: "বছর" },

  height: { en: "Height", bn: "উচ্চতা" },
  heightCm: { en: "cm", bn: "সেমি" },
  feet: { en: "ft", bn: "ফিট" },
  inches: { en: "in", bn: "ইঞ্চি" },

  weight: { en: "Weight", bn: "ওজন" },
  kg: { en: "kg", bn: "কেজি" },
  lb: { en: "lb", bn: "পাউন্ড" },

  reset: { en: "Reset", bn: "রিসেট" },
  yourBmi: { en: "Your BMI", bn: "আপনার বিএমআই" },
  category: { en: "Category", bn: "ক্যাটাগরি" },
  ideal: { en: "Ideal weight range", bn: "আদর্শ ওজনের পরিসীমা" },
  toReach: { en: "To reach a healthy BMI", bn: "স্বাস্থ্যকর বিএমআই পেতে" },
  inRange: {
    en: "You are within the healthy weight range for your height.",
    bn: "আপনি আপনার উচ্চতার জন্য স্বাস্থ্যকর ওজনের মধ্যে আছেন।",
  },
  lose: { en: "Lose {n} kg to reach BMI {b}", bn: "{n} কেজি কমালে বিএমআই {b} হবে" },
  gain: { en: "Gain {n} kg to reach BMI {b}", bn: "{n} কেজি বাড়ালে বিএমআই {b} হবে" },
  disclaimer: {
    en: "Note: South Asians have higher cardiometabolic risk at lower BMI. Consult a doctor for personalized advice.",
    bn: "মনে রাখবেন: দক্ষিণ এশীয়দের কম বিএমআইতেও হৃদরোগ ও ডায়াবেটিসের ঝুঁকি বেশি থাকে। ব্যক্তিগত পরামর্শের জন্য চিকিৎসকের সঙ্গে আলোচনা করুন।",
  },

  // Errors
  err_height_invalid: { en: "Height seems incorrect. Please check.", bn: "উচ্চতা সঠিক মনে হচ্ছে না। দয়া করে যাচাই করুন।" },
  err_weight_invalid: { en: "Weight seems incorrect. Please check.", bn: "ওজন সঠিক মনে হচ্ছে না। দয়া করে যাচাই করুন।" },
  err_age_invalid: { en: "BMI calculator is for ages 2 and above.", bn: "এই ক্যালকুলেটর ২ বছর বা তার বেশি বয়সের জন্য।" },

  // Sections
  whatTitle: { en: "What is BMI?", bn: "বিএমআই কী?" },
  what: {
    en: "Body Mass Index (BMI) is a number derived from your height and weight that gives a quick estimate of whether you are underweight, normal weight, overweight or obese. It is widely used as a screening tool by clinicians and public-health organisations.",
    bn: "বডি মাস ইনডেক্স (BMI) হলো আপনার উচ্চতা ও ওজন থেকে পাওয়া একটি সংখ্যা — যা দিয়ে দ্রুত বোঝা যায় আপনি কম ওজন, স্বাভাবিক ওজন, অতিরিক্ত ওজন না স্থূলতার শিকার। চিকিৎসক ও জনস্বাস্থ্য সংস্থাগুলো এটি স্ক্রিনিং টুল হিসেবে ব্যবহার করে।",
  },

  formulaTitle: { en: "BMI formula", bn: "বিএমআই সূত্র" },
  formulaMetric: { en: "Metric: BMI = weight (kg) / [height (m)]²", bn: "মেট্রিক: BMI = ওজন (কেজি) / [উচ্চতা (মিটার)]²" },
  formulaImperial: { en: "Imperial: BMI = (weight (lb) / [height (in)]²) × 703", bn: "ইম্পেরিয়াল: BMI = (ওজন (পাউন্ড) / [উচ্চতা (ইঞ্চি)]²) × ৭০৩" },
  formulaExample: {
    en: "Example: 70 kg ÷ (1.70 m)² = 70 ÷ 2.89 = 24.2 BMI.",
    bn: "উদাহরণ: ৭০ কেজি ÷ (১.৭০ মিটার)² = ৭০ ÷ ২.৮৯ = ২৪.২ বিএমআই।",
  },

  chartTitle: { en: "BMI chart", bn: "বিএমআই চার্ট" },

  asianTitle: { en: "Why South Asian standards matter", bn: "দক্ষিণ এশীয় মান কেন গুরুত্বপূর্ণ" },
  asianBody: {
    en: "South Asians (including Bangladeshi, Indian, Pakistani and Sri Lankan populations) tend to develop type-2 diabetes, hypertension and cardiovascular disease at lower BMI values than Western populations. The WHO 2004 expert consultation recommended action-point cut-offs of 23 (overweight) and 27.5 (obesity) for Asian adults. Bangladesh's national NCD guidelines follow the same lower thresholds, which is why we default to the Asian standard for Bangladeshi visitors.",
    bn: "দক্ষিণ এশীয়রা (বাংলাদেশী, ভারতীয়, পাকিস্তানি ও শ্রীলঙ্কান) পশ্চিমা জনগোষ্ঠীর তুলনায় কম বিএমআইতেও টাইপ-২ ডায়াবেটিস, উচ্চ রক্তচাপ ও হৃদরোগে আক্রান্ত হন। WHO ২০০৪ সালের বিশেষজ্ঞ পরামর্শে এশীয়দের জন্য ২৩ (অতিরিক্ত ওজন) ও ২৭.৫ (স্থূলতা) মাত্রা সুপারিশ করা হয়। বাংলাদেশের জাতীয় NCD নির্দেশিকাও একই কম মাত্রা অনুসরণ করে — তাই আমরা বাংলাদেশী ব্যবহারকারীদের জন্য ডিফল্ট হিসেবে দক্ষিণ এশীয় মান রাখি।",
  },

  womenTitle: { en: "BMI for women & men", bn: "নারী ও পুরুষের বিএমআই" },
  womenBody: {
    en: "The BMI formula is identical for women and men — the same number is calculated from height and weight regardless of gender. Ideal weight ranges differ by frame size, body composition and muscle mass rather than by gender alone, which is why BMI is best used as a starting point alongside waist circumference and body-fat measurements.",
    bn: "নারী ও পুরুষের জন্য বিএমআই সূত্র একই — উচ্চতা ও ওজন থেকে একইভাবে হিসাব হয়। আদর্শ ওজন মূলত শরীরের গঠন, পেশী ভর ও দেহাবয়বের উপর নির্ভর করে, শুধু লিঙ্গের উপর নয়। তাই কোমরের পরিধি ও দেহের চর্বির হিসাবের সঙ্গে বিএমআই ব্যবহার করাই সবচেয়ে ভালো।",
  },

  limitsTitle: { en: "Limitations of BMI", bn: "বিএমআইয়ের সীমাবদ্ধতা" },
  limitsBody: {
    en: "BMI does not directly measure body fat or distinguish between fat and muscle. Athletes with high muscle mass can score in the 'overweight' range despite being lean. BMI is also less accurate for pregnant women, growing children, frail older adults and people with very short or very tall stature.",
    bn: "বিএমআই সরাসরি শরীরের চর্বি মাপে না — চর্বি ও পেশীর পার্থক্য করতে পারে না। বেশি পেশীযুক্ত খেলোয়াড়রা চিকন হলেও 'ওভারওয়েট' পরিসরে পড়তে পারেন। গর্ভবতী নারী, বাড়ন্ত শিশু, দুর্বল বয়স্ক ও খুব খাটো/লম্বা মানুষের জন্যও বিএমআই কম নির্ভরযোগ্য।",
  },
};

const CATEGORY_LABEL: Record<CategoryKey, Bilingual> = {
  underweight: { en: "Underweight", bn: "কম ওজন" },
  normal: { en: "Normal weight", bn: "স্বাভাবিক ওজন" },
  overweight_at_risk: { en: "Overweight (At risk)", bn: "অতিরিক্ত ওজন (ঝুঁকিতে)" },
  overweight: { en: "Overweight", bn: "অতিরিক্ত ওজন" },
  obese_1: { en: "Obese Class I", bn: "স্থূলতা (১ম শ্রেণি)" },
  obese_2: { en: "Obese Class II", bn: "স্থূলতা (২য় শ্রেণি)" },
  obese_3: { en: "Obese Class III", bn: "স্থূলতা (৩য় শ্রেণি)" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: {
      en: "What is a healthy BMI for Bangladeshi adults?",
      bn: "বাংলাদেশী প্রাপ্তবয়স্কদের জন্য স্বাস্থ্যকর বিএমআই কত?",
    },
    a: {
      en: "Following the WHO 2004 South Asian guidance and Bangladesh national NCD guidelines, a healthy BMI for Bangladeshi adults is 18.5 to 22.9. Above 23 is considered overweight (at risk) and 25 or more is obese.",
      bn: "WHO ২০০৪ এর দক্ষিণ এশীয় নির্দেশিকা ও বাংলাদেশের জাতীয় NCD নির্দেশিকা অনুসারে প্রাপ্তবয়স্কদের স্বাস্থ্যকর বিএমআই ১৮.৫ থেকে ২২.৯। ২৩ এর উপরে অতিরিক্ত ওজন এবং ২৫ বা তার বেশি স্থূলতা ধরা হয়।",
    },
  },
  {
    q: { en: "Why are South Asian BMI thresholds lower?", bn: "দক্ষিণ এশীয় বিএমআই মাত্রা কেন কম?" },
    a: {
      en: "At the same BMI, South Asians carry more visceral (abdominal) fat and develop diabetes and heart disease at lower body weights than Europeans. WHO and the IDF therefore recommend lower action-point cut-offs for Asian populations.",
      bn: "একই বিএমআইতে দক্ষিণ এশীয়দের শরীরে পেটের ভেতরের চর্বি বেশি থাকে এবং কম ওজনেই ডায়াবেটিস ও হৃদরোগ হয়। তাই WHO ও IDF এশীয়দের জন্য কম মাত্রা সুপারিশ করে।",
    },
  },
  {
    q: { en: "Is BMI accurate for women?", bn: "নারীদের জন্য বিএমআই কি সঠিক?" },
    a: {
      en: "Yes — the formula is the same for women and men. However BMI does not distinguish fat from muscle, so combining it with waist circumference (under 80 cm for South Asian women) gives a better picture.",
      bn: "হ্যাঁ — নারী ও পুরুষের জন্য সূত্র একই। তবে এটি চর্বি ও পেশীর পার্থক্য করে না, তাই কোমরের পরিধি (দক্ষিণ এশীয় নারীদের জন্য ৮০ সেমি'র নিচে) সঙ্গে দেখা ভালো।",
    },
  },
  {
    q: { en: "How do I lower my BMI?", bn: "আমি কীভাবে বিএমআই কমাবো?" },
    a: {
      en: "A sustainable plan combines a calorie-aware diet with at least 150 minutes of moderate activity each week. Aim to lose around 0.5 kg per week — faster loss is rarely sustainable.",
      bn: "টেকসই উপায় হলো ক্যালরি নিয়ন্ত্রিত খাদ্যাভ্যাস ও সপ্তাহে অন্তত ১৫০ মিনিট মাঝারি ব্যায়াম। সপ্তাহে আনুমানিক ০.৫ কেজি কমানোর লক্ষ্য রাখুন — দ্রুত কমানো সাধারণত টেকসই নয়।",
    },
  },
  {
    q: { en: "Can BMI be wrong?", bn: "বিএমআই কি ভুল হতে পারে?" },
    a: {
      en: "BMI can mislabel muscular athletes, pregnant women, the elderly and those with non-standard frames. Use it as a starting screen, not a diagnosis.",
      bn: "পেশীবহুল খেলোয়াড়, গর্ভবতী নারী, বয়স্ক ও ভিন্ন গঠনের মানুষের ক্ষেত্রে বিএমআই বিভ্রান্তিকর হতে পারে। এটি প্রাথমিক স্ক্রিনিং, রোগ নির্ণয় নয়।",
    },
  },
  {
    q: { en: "What is BMI for kids?", bn: "শিশুদের বিএমআই কী?" },
    a: {
      en: "For children and teens (2–19 years) BMI is interpreted using age- and sex-specific percentile charts (CDC or WHO growth charts), not the adult cut-offs shown here.",
      bn: "২–১৯ বছর বয়সী শিশু-কিশোরদের জন্য বয়স ও লিঙ্গভিত্তিক পার্সেন্টাইল চার্ট (CDC বা WHO গ্রোথ চার্ট) ব্যবহার করা হয়, এখানে দেখানো প্রাপ্তবয়স্ক মাত্রা নয়।",
    },
  },
  {
    q: { en: "বাংলাদেশীদের জন্য আদর্শ বিএমআই কত?", bn: "বাংলাদেশীদের জন্য আদর্শ বিএমআই কত?" },
    a: {
      en: "১৮.৫ থেকে ২২.৯ — দক্ষিণ এশীয় মান অনুসারে এটি স্বাস্থ্যকর পরিসর।",
      bn: "১৮.৫ থেকে ২২.৯ — দক্ষিণ এশীয় মান অনুসারে এটি স্বাস্থ্যকর পরিসর।",
    },
  },
  {
    q: { en: "বিএমআই কীভাবে গণনা করা হয়?", bn: "বিএমআই কীভাবে গণনা করা হয়?" },
    a: {
      en: "ওজন (কেজি) কে উচ্চতার (মিটার) বর্গ দিয়ে ভাগ করলে বিএমআই পাওয়া যায়। উদাহরণ: ৭০ ÷ (১.৭০)² = ২৪.২।",
      bn: "ওজন (কেজি) কে উচ্চতার (মিটার) বর্গ দিয়ে ভাগ করলে বিএমআই পাওয়া যায়। উদাহরণ: ৭০ ÷ (১.৭০)² = ২৪.২।",
    },
  },
];

/* ------------ Route ------------ */

export const Route = createFileRoute("/bmi-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "BMI Calculator — South Asian & WHO Standards",
      description:
        "Free BMI calculator with South Asian standards for Bangladeshis. Find ideal weight range, view BMI chart in metric or imperial. বিএমআই ক্যালকুলেটর।",
      path: "/bmi-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "BMI Calculator", path: "/bmi-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BMICalculatorPage,
});

/* ------------ Page ------------ */

function BMICalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt1 = (n: number) => num(n.toFixed(1));

  // Internal canonical state — always metric.
  const [heightCm, setHeightCm] = useState<number | null>(170);
  const [weightKg, setWeightKg] = useState<number | null>(65);

  // Display state — mirrors metric/imperial views
  const [unit, setUnit] = useState<UnitSystem>("metric");
  const [standard, setStandard] = useState<BMIStandard>("asian");
  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState<string>("30");

  // Imperial display values, derived from metric on switch
  const [ft, setFt] = useState<string>(() => String(inchesToFtIn(cmToIn(170)).ft));
  const [inch, setInch] = useState<string>(() => String(Math.round(inchesToFtIn(cmToIn(170)).inch)));
  const [lb, setLb] = useState<string>(() => kgToLb(65).toFixed(1));

  // Metric display strings
  const [cmStr, setCmStr] = useState<string>("170");
  const [kgStr, setKgStr] = useState<string>("65");

  // Sync metric strings -> heightCm/weightKg
  useEffect(() => {
    if (unit !== "metric") return;
    const h = parseFloat(cmStr);
    setHeightCm(Number.isFinite(h) ? h : null);
  }, [cmStr, unit]);
  useEffect(() => {
    if (unit === "imperial") return;
    const w = parseFloat(kgStr);
    setWeightKg(Number.isFinite(w) ? w : null);
  }, [kgStr, unit]);

  // Sync ft/in -> heightCm (used by imperial and mixed)
  useEffect(() => {
    if (unit === "metric") return;
    const f = parseFloat(ft);
    const i = parseFloat(inch);
    if (Number.isFinite(f) || Number.isFinite(i)) {
      const totalIn = (Number.isFinite(f) ? f : 0) * 12 + (Number.isFinite(i) ? i : 0);
      setHeightCm(totalIn > 0 ? +inToCm(totalIn).toFixed(2) : null);
    } else {
      setHeightCm(null);
    }
  }, [ft, inch, unit]);
  useEffect(() => {
    if (unit !== "imperial") return;
    const p = parseFloat(lb);
    setWeightKg(Number.isFinite(p) ? +lbToKg(p).toFixed(2) : null);
  }, [lb, unit]);

  // When toggling units, refresh the OTHER system's strings from canonical metric.
  const switchUnit = (next: UnitSystem) => {
    if (next === unit) return;
    if (heightCm != null && weightKg != null) {
      if (next !== "metric") {
        const totalIn = cmToIn(heightCm);
        const { ft: f, inch: i } = inchesToFtIn(totalIn);
        setFt(String(f));
        setInch(String(Math.round(i)));
      }
      if (next === "imperial") {
        setLb(kgToLb(weightKg).toFixed(1));
      }
      if (next === "metric") {
        setCmStr(String(Math.round(heightCm)));
        setKgStr(weightKg.toFixed(1).replace(/\.0$/, ""));
      }
      if (next === "mixed") {
        setKgStr(weightKg.toFixed(1).replace(/\.0$/, ""));
      }
    }
    setUnit(next);
  };

  const ageNum = age.trim() === "" ? null : Number(age);
  const validation = useMemo(
    () => validateBMI({ heightCm, weightKg, age: ageNum }),
    [heightCm, weightKg, ageNum],
  );

  const result = useMemo(() => {
    if (!validation.ok || heightCm == null || weightKg == null) return null;
    return computeBMI({ heightCm, weightKg, standard });
  }, [validation.ok, heightCm, weightKg, standard]);

  const errKey = !validation.ok ? validation.error : null;

  const heightError =
    errKey === "height_invalid" ? tr("err_height_invalid") : null;
  const weightError = errKey === "weight_invalid" ? tr("err_weight_invalid") : null;
  const ageError = errKey === "age_invalid" ? tr("err_age_invalid") : null;

  const reset = () => {
    setUnit("metric");
    setStandard("asian");
    setGender("male");
    setAge("30");
    setCmStr("170");
    setKgStr("65");
    setFt("5");
    setInch("7");
    setLb(kgToLb(65).toFixed(1));
    setHeightCm(170);
    setWeightKg(65);
  };

  const segs = gaugeSegments(standard);

  // Convert ideal range to display unit
  const idealDisplay = result
    ? unit !== "imperial"
      ? `${fmt1(result.idealMinKg)} – ${fmt1(result.idealMaxKg)} ${tr("kg")}`
      : `${fmt1(kgToLb(result.idealMinKg))} – ${fmt1(kgToLb(result.idealMaxKg))} ${tr("lb")}`
    : "";

  const deltaDisplay = (() => {
    if (!result) return null;
    if (result.deltaKg === 0) return tr("inRange");
    const absKg = Math.abs(result.deltaKg);
    const valueStr =
      unit !== "imperial" ? `${fmt1(absKg)} ${tr("kg")}` : `${fmt1(kgToLb(absKg))} ${tr("lb")}`;
    const tmpl = result.deltaKg > 0 ? tr("lose") : tr("gain");
    return tmpl.replace("{n}", valueStr).replace("{b}", num(result.targetBmi.toFixed(1)));
  })();

  const shareLine = result
    ? `${tr("yourBmi")}: ${num(result.bmi.toFixed(1))} — ${CATEGORY_LABEL[result.category][lang]}`
    : "";

  return (
    <CalculatorLayout
      slug="bmi-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Activity"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Top toggles */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <UnitToggle<UnitSystem>
          label={tr("unitSystem")}
          value={unit}
          onChange={switchUnit}
          options={[
            { value: "metric", label: tr("metric") },
            { value: "mixed", label: tr("mixed") },
            { value: "imperial", label: tr("imperial") },
          ]}
        />
        <UnitToggle<BMIStandard>
          label={tr("standard")}
          value={standard}
          onChange={setStandard}
          options={[
            { value: "asian", label: tr("asian") },
            { value: "who", label: tr("who") },
          ]}
        />
      </div>

      {/* Inputs */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
            {tr("gender")}
          </label>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
            {(["male", "female"] as Gender[]).map((g) => {
              const active = g === gender;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {g === "male" ? tr("male") : tr("female")}
                </button>
              );
            })}
          </div>
        </div>

        <NumberInput
          label={tr("age")}
          value={age}
          onChange={setAge}
          unit={tr("ageUnit")}
          error={ageError}
          min={2}
          max={120}
        />

        {unit === "metric" ? (
          <NumberInput
            label={tr("height")}
            value={cmStr}
            onChange={setCmStr}
            unit={tr("heightCm")}
            error={heightError}
            placeholder="170"
          />
        ) : (
          <div className="space-y-1.5">
            <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("height")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <NumberInput label="" value={ft} onChange={setFt} unit={tr("feet")} />
              <NumberInput label="" value={inch} onChange={setInch} unit={tr("inches")} />
            </div>
            {heightError && (
              <p className={cn("text-xs text-destructive", lang === "bn" && "font-bn")}>{heightError}</p>
            )}
          </div>
        )}

        {unit === "imperial" ? (
          <NumberInput
            label={tr("weight")}
            value={lb}
            onChange={setLb}
            unit={tr("lb")}
            error={weightError}
            placeholder="143"
          />
        ) : (
          <NumberInput
            label={tr("weight")}
            value={kgStr}
            onChange={setKgStr}
            unit={tr("kg")}
            error={weightError}
            placeholder="65"
          />
        )}
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {/* Result */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary */}
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p
                  className={cn(
                    "text-xs font-semibold uppercase tracking-wide text-accent",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {tr("yourBmi")}
                </p>
                <p className="mt-1 text-4xl font-semibold tabular-nums text-foreground md:text-5xl">
                  {num(result.bmi.toFixed(1))}
                </p>
                <p className={cn("mt-2 text-sm font-medium", lang === "bn" && "font-bn")}>
                  {tr("category")}: <span className="text-foreground">{CATEGORY_LABEL[result.category][lang]}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({standard === "asian" ? tr("asian") : tr("who")})
                  </span>
                </p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>

            {/* Gauge */}
            <div className="mt-5">
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
                <div className="absolute inset-0 flex">
                  {segs.map((s) => {
                    const min = 12, max = 40;
                    const width = ((Math.min(s.to, max) - Math.max(s.from, min)) / (max - min)) * 100;
                    return <div key={s.key} className={cn("h-full", s.color)} style={{ width: `${width}%` }} />;
                  })}
                </div>
                <motion.div
                  initial={{ left: "0%" }}
                  animate={{ left: `${result.gaugePosition * 100}%` }}
                  transition={{ type: "spring", stiffness: 120, damping: 18 }}
                  className="absolute -top-1 h-5 w-1 -translate-x-1/2 rounded-sm bg-foreground shadow"
                />
              </div>
              <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted-foreground">
                <span>{num(12)}</span>
                <span>{num(18.5)}</span>
                <span>{num(standard === "asian" ? 23 : 25)}</span>
                <span>{num(30)}</span>
                <span>{num(40)}</span>
              </div>
            </div>
          </div>

          {/* Secondary info */}
          <div className="grid gap-3 md:grid-cols-2">
            <InfoCard
              icon={<Target size={16} className="text-accent" />}
              label={tr("ideal")}
              value={idealDisplay}
              lang={lang}
            />
            <InfoCard
              icon={
                result.deltaKg > 0 ? (
                  <TrendingDown size={16} className="text-accent" />
                ) : result.deltaKg < 0 ? (
                  <TrendingUp size={16} className="text-accent" />
                ) : (
                  <Activity size={16} className="text-accent" />
                )
              }
              label={tr("toReach")}
              value={deltaDisplay ?? ""}
              lang={lang}
            />
          </div>

          {/* Disclaimer */}
          <div
            className={cn(
              "flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
              lang === "bn" && "font-bn",
            )}
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      {/* Content */}
      <article className="mt-12 space-y-10">
        <Section title={tr("whatTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("what")}</p>
        </Section>

        <Section title={tr("formulaTitle")} lang={lang}>
          <ul className={cn("space-y-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{tr("formulaMetric")}</li>
            <li>{tr("formulaImperial")}</li>
            <li className="text-foreground">{tr("formulaExample")}</li>
          </ul>
        </Section>

        <Section title={tr("chartTitle")} lang={lang}>
          <BMIChart lang={lang} num={num} />
        </Section>

        <Section title={tr("asianTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("asianBody")}</p>
        </Section>

        <Section title={tr("womenTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("womenBody")}</p>
        </Section>

        <Section title={tr("limitsTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("limitsBody")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ------------ Helpers ------------ */

function InfoCard({
  icon,
  label,
  value,
  lang,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  lang: "en" | "bn";
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10">
        {icon}
      </span>
      <div className="min-w-0">
        <p className={cn("text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</p>
        <p className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

function BMIChart({ lang, num }: { lang: "en" | "bn"; num: (n: number | string) => string }) {
  const rows: { range: string; asian: Bilingual; who: Bilingual; color: string }[] = [
    {
      range: `< ${num(18.5)}`,
      asian: CATEGORY_LABEL.underweight,
      who: CATEGORY_LABEL.underweight,
      color: "bg-sky-400/30",
    },
    {
      range: `${num(18.5)} – ${num(22.9)}`,
      asian: CATEGORY_LABEL.normal,
      who: CATEGORY_LABEL.normal,
      color: "bg-emerald-500/30",
    },
    {
      range: `${num(23.0)} – ${num(24.9)}`,
      asian: CATEGORY_LABEL.overweight_at_risk,
      who: CATEGORY_LABEL.normal,
      color: "bg-yellow-400/30",
    },
    {
      range: `${num(25.0)} – ${num(29.9)}`,
      asian: CATEGORY_LABEL.obese_1,
      who: CATEGORY_LABEL.overweight,
      color: "bg-orange-500/30",
    },
    {
      range: `${num(30.0)} – ${num(34.9)}`,
      asian: CATEGORY_LABEL.obese_2,
      who: CATEGORY_LABEL.obese_1,
      color: "bg-red-500/30",
    },
    {
      range: `${num(35.0)} – ${num(39.9)}`,
      asian: CATEGORY_LABEL.obese_2,
      who: CATEGORY_LABEL.obese_2,
      color: "bg-red-600/30",
    },
    {
      range: `≥ ${num(40.0)}`,
      asian: CATEGORY_LABEL.obese_2,
      who: CATEGORY_LABEL.obese_3,
      color: "bg-red-700/30",
    },
  ];
  const head = {
    bmi: { en: "BMI", bn: "বিএমআই" },
    asian: { en: "South Asian", bn: "দক্ষিণ এশীয়" },
    who: { en: "WHO", bn: "WHO" },
  };
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{head.bmi[lang]}</th>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{head.asian[lang]}</th>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{head.who[lang]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-3 py-2 tabular-nums">
                <span className={cn("inline-block rounded px-2 py-0.5 text-foreground", r.color)}>{r.range}</span>
              </td>
              <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{r.asian[lang]}</td>
              <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{r.who[lang]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
